import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, ease as mmEase, useInView } from "@/lib/motion";
import FloatingCurrencyButton from "../components/FloatingCurrencyButton";

const ez = mmEase.expo;

// Families photo (design_guidelines.json: users_receiving_money)
const FAMILIES_PHOTO = "https://images.unsplash.com/photo-1758599668780-60d53c35135d?w=1400&q=75&auto=format&fit=crop";

// The Close: IntersectionObserver thresholds → progressive Thread opacity
const THRESHOLDS = [0.1, 0.3, 0.5, 0.8];
const OPACITIES  = [0.12, 0.28, 0.48, 0.72];

export default function EmotionalEnding({ goToApp }) {
  const navigate  = useNavigate();
  const ref       = useRef(null);
  const inView    = useInView(ref, { once: true, margin: "-60px 0px" });
  const [threadOpacity, setThreadOpacity] = useState(0.18);
  const [multiPacket,   setMultiPacket  ] = useState(false);

  // The Close: scroll-linked Thread brightness
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const ratio = entry.intersectionRatio;
        if (ratio >= 0.8) {
          setThreadOpacity(0.72);
          setMultiPacket(true);
        } else if (ratio >= 0.5) {
          setThreadOpacity(0.48);
        } else if (ratio >= 0.3) {
          setThreadOpacity(0.28);
        } else if (ratio >= 0.1) {
          setThreadOpacity(0.12);
        }
      },
      { threshold: THRESHOLDS },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative py-32 px-6 overflow-hidden bg-void">

      {/* Families photo backdrop — The Close bg */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ opacity: inView ? 1 : 0, transition: "opacity 1.2s ease" }}
      >
        <img
          src={FAMILIES_PHOTO}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            filter: "brightness(0.45) contrast(1.1) saturate(0.75)",
          }}
        />
        {/* Duotone overlay */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(140deg, rgba(3,9,16,0.82) 0%, rgba(16,185,129,0.08) 100%)",
        }} />
        {/* Radial vignette */}
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse 80% 70% at 50% 100%, transparent 30%, rgba(3,9,16,0.90) 75%)",
        }} />
      </div>

      {/* Atmospheric depth glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div style={{
          position: "absolute", top: "25%", left: "50%", transform: "translateX(-50%)",
          width: "72vw", height: "52vh", borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(16,185,129,0.09) 0%, transparent 65%)",
          filter: "blur(72px)",
        }} />
      </div>

      {/* Wide brand curve — The Close: opacity driven by scroll depth */}
      <div
        className="absolute inset-x-0 pointer-events-none"
        aria-hidden="true"
        style={{
          top: "50%",
          transform: "translateY(-50%)",
          opacity: threadOpacity,
          transition: "opacity 0.8s ease",
        }}
      >
        <svg viewBox="0 0 1200 120" fill="none" style={{ width: "100%", overflow: "visible" }}>
          <defs>
            <linearGradient id="emGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="var(--color-success)" stopOpacity="0" />
              <stop offset="25%"  stopColor="var(--color-success)" />
              <stop offset="75%"  stopColor="var(--mm-cyan)" />
              <stop offset="100%" stopColor="var(--mm-cyan)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            id="em-curve"
            d="M 0 60 C 200 20, 400 100, 600 60 S 1000 20, 1200 60"
            stroke="url(#emGrad)" strokeWidth="1.5" fill="none" strokeDasharray="8 6"
            style={{ animation: "dashMove 2s linear infinite" }}
          />
          {/* Endpoint anchors */}
          <circle cx="0"    cy="60" r="5" fill="none" stroke="var(--color-success)" strokeWidth="1.2" opacity="0.4" />
          <circle cx="0"    cy="60" r="2.5" fill="var(--color-success)" opacity="0.6" />
          <circle cx="1200" cy="60" r="5" fill="none" stroke="var(--color-success)" strokeWidth="1.2" opacity="0.4" />
          <circle cx="1200" cy="60" r="2.5" fill="var(--color-success)" opacity="0.6" />

          {/* Primary packet */}
          <circle r="9" fill="var(--color-success)" opacity="0.5">
            <animateMotion dur="6s" repeatCount="indefinite"><mpath href="#em-curve" /></animateMotion>
          </circle>
          <circle r="5" fill="var(--color-success)">
            <animateMotion dur="6s" repeatCount="indefinite"><mpath href="#em-curve" /></animateMotion>
          </circle>
          <circle r="2.5" fill="white">
            <animateMotion dur="6s" repeatCount="indefinite"><mpath href="#em-curve" /></animateMotion>
          </circle>

          {/* The Close: two additional packets appear at scroll depth 0.8 */}
          {multiPacket && (
            <>
              <circle r="7" fill="var(--color-success)" opacity="0.35">
                <animateMotion dur="6s" begin="2s" repeatCount="indefinite"><mpath href="#em-curve" /></animateMotion>
              </circle>
              <circle r="4" fill="var(--color-success)" opacity="0.7">
                <animateMotion dur="6s" begin="2s" repeatCount="indefinite"><mpath href="#em-curve" /></animateMotion>
              </circle>
              <circle r="6" fill="var(--color-success)" opacity="0.28">
                <animateMotion dur="6s" begin="4s" repeatCount="indefinite"><mpath href="#em-curve" /></animateMotion>
              </circle>
              <circle r="3.5" fill="var(--color-success)" opacity="0.65">
                <animateMotion dur="6s" begin="4s" repeatCount="indefinite"><mpath href="#em-curve" /></animateMotion>
              </circle>
            </>
          )}
        </svg>
      </div>

      <div className="relative z-10 max-w-[800px] mx-auto text-center">

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, ease: ez, delay: 0.1 }}
          className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald mb-6"
        >
          Wherever life takes you
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
          animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
          transition={{ duration: 0.9, ease: ez, delay: 0.12 }}
          className="font-display font-black text-white text-balance leading-[1.04] mb-7"
          style={{ fontSize: "clamp(2.2rem, 5.5vw, 4.4rem)", letterSpacing: "-0.04em" }}
        >
          Your family is still<br />
          <span className="text-emerald">one transfer away.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: ez, delay: 0.22 }}
          className="text-[1.05rem] leading-relaxed mb-12 mx-auto"
          style={{ color: "rgba(255,255,255,0.44)", maxWidth: "480px", fontStyle: "italic" }}
        >
          Every family. Every corridor. Every time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: ez, delay: 0.30 }}
          className="flex flex-wrap items-center justify-center gap-3 mb-8"
        >
          <FloatingCurrencyButton
            data-testid="footer-cta-start"
            onClick={goToApp}
            label="Open free account"
          />
          <button
            onClick={() => navigate("/support#contact")}
            style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              padding: "14px 28px", borderRadius: "100px",
              fontSize: "15px", fontWeight: 600,
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.48)",
              background: "none", cursor: "pointer", transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.24)";
              e.currentTarget.style.color = "rgba(255,255,255,0.72)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
              e.currentTarget.style.color = "rgba(255,255,255,0.48)";
            }}
          >
            Talk to a human
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.44, ease: ez, delay: 0.42 }}
          style={{ fontSize: "11px", color: "rgba(255,255,255,0.20)" }}
        >
          No monthly fee · No minimum balance · Cancel anytime
        </motion.p>

      </div>
    </section>
  );
}
